import React, { useEffect, useState } from 'react'
import { Button, Modal, Select, message } from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { US_STATE_OPTIONS } from 'enrollment-platform/pages/PlanManagementV2/pages/PlanOverrides/constants'
import {
  NO_PLANS_AVAILABLE_MESSAGE,
  type CarrierDownloadType,
  type CarrierResponseDto,
  type TaskResponseDto,
  downloadCarrierFile,
  downloadableStates,
  formatTaskStates,
  isNoPlansAvailableError,
  getCarriers,
  getDownloadableCarrierTasks,
} from '../api/fetch-carrier-api'
import styles from '../PlanManagementV3.module.scss'

const CARRIER_DOWNLOAD_TYPE_OPTIONS: {
  label: string
  value: CarrierDownloadType
}[] = [
  { label: 'Availability Check', value: 'PLAN_AVAILABILITY_BY_ZIP' },
  { label: 'Comparison Report', value: 'COMPARISON_REPORT' },
  { label: 'Plans and Rates', value: 'PLANS_AND_RATES' },
  { label: 'Service Areas', value: 'SERVICE_AREAS' },
]

const VALID_DOWNLOAD_TYPES = new Set<string>(
  CARRIER_DOWNLOAD_TYPE_OPTIONS.map((o) => o.value)
)

interface InitialValues {
  groupCode?: string
  taskId?: string
  downloadType?: CarrierDownloadType
  state?: string
}

interface Props {
  open: boolean
  onClose: () => void
  /**
   * Seed the modal's filters from a deeplink. Applied once when the modal
   * transitions from closed → open; not re-applied on subsequent URL changes
   * so mid-session user selections are not overwritten.
   */
  initialValues?: InitialValues
  /**
   * Called whenever a filter value changes while the modal is open so the
   * parent can keep the URL in sync with the current selection.
   */
  onValuesChange?: (values: InitialValues) => void
}

function formatTaskLabel(task: TaskResponseDto): string {
  const ts = dayjs(task.createdAt).format('MMM D, YYYY h:mm A')
  return `${ts} · ${formatTaskStates(task.state)}`
}

export default function DownloadCarrierCsvModal({
  open,
  onClose,
  initialValues,
  onValuesChange,
}: Props) {
  const [groupCode, setGroupCode] = useState<string | undefined>(undefined)
  const [taskId, setTaskId] = useState<string | undefined>(undefined)
  const [downloadType, setDownloadType] = useState<
    CarrierDownloadType | undefined
  >(undefined)
  const [state, setState] = useState<string | undefined>(undefined)
  const [isDownloading, setIsDownloading] = useState(false)

  const {
    data: carriers,
    isLoading: isLoadingCarriers,
    error: carriersError,
  } = useQuery({
    queryKey: ['planBotCarriers', 'active'],
    queryFn: () => getCarriers({ isActive: true }),
    enabled: open,
  })

  const {
    data: tasksResponse,
    isLoading: isLoadingTasks,
    isPending: isPendingTasks,
    error: tasksError,
  } = useQuery({
    queryKey: ['carrierDownloadTasks', groupCode],
    queryFn: () => getDownloadableCarrierTasks(groupCode as string),
    enabled: open && groupCode !== undefined,
  })

  const carrierOptions = Array.from(
    new Map(
      (Array.isArray(carriers) ? carriers : []).map((c: CarrierResponseDto) => [
        c.groupCode,
        { label: c.carrierName ?? c.groupCode, value: c.groupCode },
      ])
    ).values()
  )

  const taskRows = tasksResponse?.data ?? []
  const taskOptions = taskRows.map((t: TaskResponseDto) => ({
    label: formatTaskLabel(t),
    value: t.id,
  }))
  const selectedTask = taskRows.find((t: TaskResponseDto) => t.id === taskId)
  const taskStateSet = new Set(downloadableStates(selectedTask))
  const stateOptions = selectedTask
    ? US_STATE_OPTIONS.filter((opt) => taskStateSet.has(opt.value))
    : []

  // ─── Error toasts ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (carriersError) {
      console.error('Could not load carriers', carriersError)
      message.error('Failed to load carriers.')
    }
  }, [carriersError])

  useEffect(() => {
    if (tasksError) {
      console.error('Could not load fetch versions', tasksError)
      message.error('Failed to load fetch versions.')
    }
  }, [tasksError])

  // ─── Seed / clear on open toggle ────────────────────────────────────────────
  //
  // Resets are intentionally NOT done via cascading effects keyed on each field
  // (e.g. [groupCode] → clear taskId) because that pattern fights with seeding
  // all 4 fields simultaneously from a deeplink. Instead, dependent fields are
  // cleared inline inside the onChange handlers below so seeding is always safe.

  useEffect(() => {
    if (!open) {
      setGroupCode(undefined)
      setTaskId(undefined)
      setDownloadType(undefined)
      setState(undefined)
      return
    }
    // Seed from deeplink values once per open transition.
    // Static CARRIER_DOWNLOAD_TYPE_OPTIONS can be validated synchronously here.
    const seededDownloadType =
      initialValues?.downloadType &&
      VALID_DOWNLOAD_TYPES.has(initialValues.downloadType)
        ? initialValues.downloadType
        : undefined
    setGroupCode(initialValues?.groupCode)
    setTaskId(initialValues?.taskId)
    setDownloadType(seededDownloadType)
    setState(initialValues?.state)
  }, [open])

  // ─── Validate seeded taskId once task list loads ─────────────────────────────
  //
  // If the deeplinked taskId no longer exists for this carrier, silently clear it
  // (and the dependent state) so the Download button can't be enabled by a stale
  // link value that doesn't appear in the Select.

  useEffect(() => {
    if (!taskId) return
    if (groupCode === undefined) return
    // Still pending (no data yet) — wait before validating.
    // In TanStack Query v5 `isLoading` is only true during the initial fetch;
    // `isPending` stays true while the query is idle, disabled, or fetching for
    // the first time, preventing us from treating an empty list as "loaded".
    if (isPendingTasks) return
    const isValid = taskOptions.some((o) => o.value === taskId)
    if (!isValid) {
      setTaskId(undefined)
      setState(undefined)
    }
  }, [isPendingTasks, taskOptions])

  // ─── Validate seeded state once task's state list is known ──────────────────

  useEffect(() => {
    if (!state || !taskId) return
    if (stateOptions.length === 0) return
    const isValid = stateOptions.some((o) => o.value === state)
    if (!isValid) setState(undefined)
  }, [stateOptions])

  // ─── Sync current values to URL ─────────────────────────────────────────────

  useEffect(() => {
    if (!open) return
    onValuesChange?.({ groupCode, taskId, downloadType, state })
  }, [open, groupCode, taskId, downloadType, state, onValuesChange])

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleClose = () => {
    if (isDownloading) return
    onClose()
  }

  const handleDownload = async () => {
    if (!taskId || !downloadType) return
    setIsDownloading(true)
    try {
      await downloadCarrierFile({
        taskId,
        downloadType,
        state,
      })
      handleClose()
    } catch (err) {
      if (isNoPlansAvailableError(err)) {
        message.warning(NO_PLANS_AVAILABLE_MESSAGE)
        return
      }
      console.error('Failed to download carrier file', err)
      message.error('Download failed. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  const isDownloadDisabled =
    !groupCode || !taskId || !downloadType || isDownloading

  return (
    <Modal
      open={open}
      title="Download CSV"
      maskClosable={false}
      centered
      destroyOnClose
      onCancel={handleClose}
      footer={[
        <Button key="cancel" htmlType="button" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="download"
          htmlType="button"
          type="primary"
          disabled={isDownloadDisabled}
          loading={isDownloading}
          onClick={() => void handleDownload()}
        >
          Download
        </Button>,
      ]}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Carrier</span>
          <Select
            placeholder="Select a carrier"
            loading={isLoadingCarriers}
            showSearch
            optionFilterProp="label"
            value={groupCode}
            onChange={(v) => {
              setGroupCode(v as string)
              setTaskId(undefined)
              setState(undefined)
            }}
            options={carrierOptions}
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>
            Fetch version
            {selectedTask && ` - Plan Year ${selectedTask.year}`}
          </span>
          <Select
            placeholder="Select a fetch version"
            loading={isLoadingTasks}
            disabled={!groupCode}
            value={taskId}
            onChange={(v) => {
              setTaskId(v as string)
              setState(undefined)
            }}
            options={taskOptions}
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Data type</span>
          <Select
            placeholder="Select a data type"
            value={downloadType}
            onChange={(v) => setDownloadType(v as CarrierDownloadType)}
            options={CARRIER_DOWNLOAD_TYPE_OPTIONS}
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>State</span>
          <Select
            placeholder={
              taskId
                ? 'All states in this fetch'
                : 'Select a fetch version first'
            }
            showSearch
            optionFilterProp="label"
            disabled={!taskId}
            allowClear
            value={state}
            onChange={(v) => setState(v as string | undefined)}
            options={stateOptions}
          />
        </div>
      </div>
    </Modal>
  )
}
